// frontend/src/features/carc/CarcPage.jsx
"use client";

import React, { useState, useCallback, useMemo, useRef } from "react";
import api from "../../apiClient";
import { useNavigate } from "react-router-dom";

/* ---------- small utils ---------- */
const cn = (...xs) => xs.filter(Boolean).join(" ");
const fmtBytes = (n) => {
  if (n == null) return "";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, x = n;
  while (x >= 1024 && i < u.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(x < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

export default function CarcPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  /* ---------- file intake ---------- */
  const acceptXlsx = (list) =>
    Array.from(list || []).reduce(
      (acc, f) => {
        f?.name?.toLowerCase().endsWith(".xlsx")
          ? acc.ok.push(f)
          : acc.bad.push(f?.name || "unnamed");
        return acc;
      },
      { ok: [], bad: [] }
    );

  const onFilesPicked = useCallback((picked) => {
    if (!picked) return;
    const { ok, bad } = acceptXlsx(picked);
    setFiles((prev) => [...prev, ...ok]);
    setRejected((prev) => [...prev, ...bad]);
    setSummary(null);
    if (bad.length) setError(`Ignored: ${bad.join(", ")} (only .xlsx supported)`);
  }, []);

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    onFilesPicked(e.dataTransfer.files);
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; };

  const removeAt = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setSummary(null);
  };
  const clearAll = () => { setFiles([]); setRejected([]); setSummary(null); setError(""); };

  const canAct = files.length > 0 && !busy;
  const totalSize = useMemo(() => files.reduce((s, f) => s + (f.size || 0), 0), [files]);

  /* ---------- backend calls (UNCHANGED endpoints) ---------- */
  async function previewSummary() {
    if (!files.length || busy) return;
    setBusy(true); setError(""); setSummary(null);
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      const res = await api.post("/api/carc/upload", form); // unchanged
      const data = res.data;
      setSummary(data.summary || data);
    } catch (e) {
      setError(e?.message || "Failed to preview");
    } finally {
      setBusy(false);
    }
  }

  async function downloadZip() {
    if (!files.length || busy) return;
    setBusy(true); setError("");
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));

      const res = await api.post("/api/carc/download", form, { // unchanged
        responseType: "blob",
        validateStatus: (s) => s >= 200 && s < 500,
      });

      const ctype = (res.headers?.["content-type"] || "").toLowerCase();
      if (!ctype.includes("application/zip")) {
        const text = await res.data.text().catch(() => "");
        throw new Error(text || `Download failed (${res.status})`);
      }

      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "carc_outputs.zip";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || "Failed to download");
    } finally {
      setBusy(false);
    }
  }

  /* ---------- UI ---------- */
  return (
    <div className="min-h-[calc(100vh-64px)] pb-24">
      {/* Centered container at ~90% width */}
      <div className="max-w-[90vw] mx-auto px-4">
        {/* Top spacer */}
        <div className="h-4 md:h-6" />
        {/* Header (Subtle Card) */}
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="p-4 md:p-5">
            <div className="flex items-center gap-3">
              <h1 className="text-xl md:text-2xl font-semibold tracking-tight text-slate-900">
                CARC — <span className="whitespace-nowrap">Capitol Appraisal</span> Roll Cleaner
              </h1>
            </div>
            <p className="mt-2 text-slate-600">
              Upload one or more <strong>.xlsx</strong> roll files. We’ll return{" "}
              <code className="px-1 rounded bg-slate-100">cleaned_*.xlsx</code> for each and a combined{" "}
              <code className="px-1 rounded bg-slate-100">master_cleaned.xlsx</code>.
            </p>
          </div>
        </div>

        {/* Uploader */}
        <div
          role="button"
          tabIndex={0}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            "rounded-2xl border-2 border-dashed transition",
            "border-slate-300 hover:border-slate-400 hover:bg-slate-50",
            "bg-white p-6 md:p-8 shadow-sm"
          )}
        >
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-slate-100">
              <span className="text-2xl">⬆️</span>
            </div>
            <div>
              <p className="text-slate-800 font-medium">
                Drag & drop Capitol Appraisal <code className="px-1 rounded bg-slate-100">.xlsx</code> files here
              </p>
              <p className="text-slate-500 text-sm mt-1">or click to choose files from your computer</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              multiple
              onChange={(e) => onFilesPicked(e.target.files)}
              className="hidden"
            />
          </div>
        </div>

        {/* Files list */}
        {files.length > 0 && (
          <div className="mt-6 rounded-2xl bg-white shadow-sm border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-slate-700 font-medium">Selected files</div>
              <div className="text-xs text-slate-500">
                {files.length} file{files.length > 1 ? "s" : ""} • {fmtBytes(totalSize)}
              </div>
            </div>
            <ul className="max-h-[40vh] overflow-auto divide-y divide-slate-100">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="px-5 py-3 flex items-center gap-3">
                  <div className="h-8 w-8 flex items-center justify-center rounded-md bg-slate-100">📄</div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-slate-800">{f.name}</div>
                    <div className="text-xs text-slate-500">{fmtBytes(f.size)}</div>
                  </div>
                  <button
                    onClick={() => removeAt(i)}
                    className="text-slate-600 hover:text-rose-600 text-sm px-2 py-1 rounded-md hover:bg-rose-50"
                    aria-label={`Remove ${f.name}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>

            <div className="px-5 py-3 flex items-center justify-between">
              <button
                onClick={clearAll}
                className="text-slate-600 hover:text-slate-800 text-sm px-2 py-1 rounded-md hover:bg-slate-50"
              >
                Clear all
              </button>

              <div className="flex gap-2">
                <button
                  onClick={previewSummary}
                  disabled={!canAct}
                  className={cn(
                    "px-3.5 py-2 rounded-lg text-sm font-medium",
                    canAct ? "bg-slate-800 text-white hover:bg-slate-900" : "bg-slate-200 text-slate-500"
                  )}
                >
                  {busy ? "Working…" : "Preview Summary"}
                </button>
                <button
                  onClick={downloadZip}
                  disabled={!canAct}
                  className={cn(
                    "px-3.5 py-2 rounded-lg text-sm font-medium",
                    canAct ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-emerald-200 text-emerald-700/60"
                  )}
                >
                  {busy ? "Building Zip…" : "Download Zip"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rejected notice */}
        {rejected.length > 0 && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
            Ignored: {rejected.join(", ")} (only .xlsx supported)
          </div>
        )}

        {/* Summary */}
        {summary && (
          <div className="mt-6 grid grid-cols-1 gap-4">
            <div className="rounded-2xl bg-white shadow-sm border border-slate-200">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className="text-slate-700 font-medium">Summary</div>
                <div className="text-xs text-slate-500">Processed {summary.processed_files ?? 0} file(s)</div>
              </div>

              <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                <Stat label="Parser version" value={summary.parser_version || "—"} />
                <Stat label="Total rows" value={(summary.total_rows ?? 0).toLocaleString()} />
                <Stat label="Outputs" value={(summary.outputs || []).length} />

                {summary.module_path && (
                  <div className="md:col-span-3">
                    <Label>Module path</Label>
                    <Code>{summary.module_path}</Code>
                  </div>
                )}

                <div className="md:col-span-3">
                  <Label>Files</Label>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {(summary.files || []).map((f, i) => (
                      <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                        <div className="text-slate-800 font-medium truncate">{f.file}</div>
                        <div className="text-sm text-slate-600">
                          Rows: <span className="font-medium">{(f.row_count ?? 0).toLocaleString()}</span>
                          {f.first_row_lease ? (
                            <> • First lease: <span className="font-medium">{f.first_row_lease}</span></>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="md:col-span-3">
                  <Label>Raw JSON</Label>
                  <pre className="mt-2 text-xs bg-slate-900 text-slate-100 rounded-xl p-3 overflow-auto max-h-[40vh]">
                    {JSON.stringify(summary, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error toast */}
      {error && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2">
          <div className="rounded-xl border border-rose-200 bg-white text-rose-700 shadow-lg px-4 py-2">
            {error}
            <button onClick={() => setError("")} className="ml-3 text-rose-700/70 hover:text-rose-800 underline text-sm">dismiss</button>
          </div>
        </div>
      )}

      {/* Floating Apps button — unchanged behavior */}
      <button
        onClick={() => navigate("/apps")}
        className="fixed bottom-5 left-5 z-50 rounded-xl border border-gray-200 bg-white/90 backdrop-blur px-3 py-2 text-sm shadow hover:shadow-md hover:border-gray-300"
        aria-label="Open App Suite"
        title="Open App Suite"
      >
        <span className="inline-flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" />
          </svg>
          Apps
        </span>
      </button>
    </div>
  );
}

/* tiny presentational bits */
function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-800">{String(value)}</div>
    </div>
  );
}
function Label({ children }) {
  return <div className="text-xs uppercase tracking-wide text-slate-500">{children}</div>;
}
function Code({ children }) {
  return <code className="block w-full overflow-x-auto rounded-lg bg-slate-100 p-2 text-xs text-slate-800">{children}</code>;
}
